import React, { useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Lock, Sparkles, Maximize2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Section, Mono, Swap, useAudience } from "./NetworkAtoms";

/** Real screenshots of the live portals — demo data only, captured from the running product. */
const FRAMES = [
  {
    src: "/network/introducer-dashboard.webp",
    tag: "Introducer portal",
    title: "Your desk, the moment you log in",
    body: "Commission terms, next payout, what you're owed, what's been paid, and every case you've introduced — one screen, no spreadsheet.",
    audience: "introducer",
  },
  {
    src: "/network/lender-desk.webp",
    tag: "Lender desk",
    title: "Deal flow you can work in a morning",
    body: "Applicant, business, finance type, amount, term and a status control on every row. Cases waiting too long flag themselves.",
    audience: "lender",
  },
  {
    src: "/network/introducer-apply-links.webp",
    tag: "Introducer portal",
    title: "Your own application link and QR code",
    body: "Put it in your signature, on your site or on a card. Your client applies and uploads their own documents — and it lands credited to you.",
    audience: "introducer",
  },
  {
    src: "/network/lender-criteria.webp",
    tag: "Lender desk",
    title: "You set the criteria. The router respects it",
    body: "Amount band, sectors, terms, geography, turnaround. Change it at 9am and the next routing decision honours it.",
    audience: "lender",
  },
  {
    src: "/network/introducer-documents.webp",
    tag: "Both portals",
    title: "Agreement and mutual NDA, already signed by us",
    body: "Countersign online in under two minutes. Every version filed, every change emailed, nothing sitting in a printer tray.",
    audience: "both",
  },
  {
    src: "/network/customer-apply.webp",
    tag: "What your client sees",
    title: "Branded, mobile, three minutes",
    body: "Saves as they type so they can start on a phone and finish on a laptop. Consent captured and timestamped, documents attached.",
    audience: "both",
  },
];

const HEAD = {
  introducer: {
    title: "Inside the members' area",
    lede: "These are real screens from the live introducer portal and lender desk — demo data, nothing else changed. You get access the day you're approved.",
  },
  lender: {
    title: "Inside the members' area",
    lede: "These are real screens from the live lender desk and introducer portal — demo data, nothing else changed. Your desk is set up before your first case arrives.",
  },
};

export default function MembersReveal() {
  const { audience } = useAudience();
  const [zoom, setZoom] = useState(null);
  const c = HEAD[audience];
  const ordered = [...FRAMES].sort((a, b) => {
    const rank = (x) => (x.audience === audience ? 0 : x.audience === "both" ? 1 : 2);
    return rank(a) - rank(b);
  });

  return (
    <Section eyebrow="Invited members only" testId="network-members"
             title={<Swap k={`mr-t-${audience}`}>{c.title}</Swap>}>
      <Swap k={`mr-l-${audience}`} className="mt-4 max-w-2xl text-[15px] sm:text-base text-slate-400 leading-relaxed">
        {c.lede}
      </Swap>

      <div className="mt-14 space-y-16 sm:space-y-24">
        {ordered.map((f, i) => (
          <Frame key={f.src} frame={f} flip={i % 2 === 1} index={i} onZoom={() => setZoom(f)} />
        ))}
      </div>

      <div className="mt-14 flex items-start gap-3.5 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 sm:p-6"
           data-testid="members-demo-note">
        <ShieldCheck className="size-4 shrink-0 mt-0.5 text-emerald-400/80" />
        <p className="text-[13.5px] sm:text-[14.5px] leading-relaxed text-slate-400">
          <span className="text-slate-200">Every screen above is the live Connexis product</span> — not a mock-up or
          a design concept. The names, amounts and documents you can see belong to a demo account we keep for exactly
          this purpose: real partner and client information never leaves the portal, and it is never published here.
        </p>
      </div>

      <Dialog open={!!zoom} onOpenChange={(v) => !v && setZoom(null)}>
        <DialogContent className="max-w-[96vw] sm:max-w-5xl bg-[#0B1220] border-white/12 p-3" data-testid="members-lightbox">
          {zoom && (
            <>
              <DialogTitle className="sr-only">{zoom.title}</DialogTitle>
              <Mono>{zoom.tag}</Mono>
              <img src={zoom.src} alt={zoom.title} className="mt-2 w-full rounded-lg" />
              <p className="text-[12.5px] text-slate-400">{zoom.title}</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Section>
  );
}

function Frame({ frame, flip, index, onZoom }) {
  const reduce = useReducedMotion();
  const [revealed, setRevealed] = useState(!!reduce);
  const ref = useRef(null);

  return (
    <div ref={ref} className={`grid lg:grid-cols-12 gap-7 lg:gap-10 items-center ${flip ? "" : ""}`}
         data-testid={`members-frame-${index}`}>
      {/* Caption */}
      <div className={`lg:col-span-4 ${flip ? "lg:order-2" : ""}`}>
        <Mono>{frame.tag}</Mono>
        <h3 className="mt-3 font-cabinet text-[24px] sm:text-[28px] leading-[1.1] tracking-[-0.02em] font-semibold text-white">
          {frame.title}
        </h3>
        <p className="mt-3 text-[14px] leading-relaxed text-slate-400">{frame.body}</p>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 h-7">
          <Sparkles className="size-3 text-[#7da9ff]" />
          <span className="text-[11px] text-slate-400">{revealed ? "Live product" : "Scroll to unlock"}</span>
        </div>
      </div>

      {/* Device frame */}
      <motion.div
        className={`lg:col-span-8 ${flip ? "lg:order-1" : ""}`}
        initial={reduce ? false : { opacity: 0, y: 26 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-90px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        onViewportEnter={() => setRevealed(true)}
      >
        <div className="relative rounded-2xl border border-white/12 bg-[#0B1220] p-1.5 shadow-[0_50px_120px_-40px_rgba(0,85,255,0.45)]">
          {/* chrome */}
          <div className="flex items-center gap-1.5 px-3 py-2">
            <span className="size-2 rounded-full bg-white/15" />
            <span className="size-2 rounded-full bg-white/10" />
            <span className="size-2 rounded-full bg-white/10" />
            <span className="ml-3 font-mono text-[9.5px] text-slate-600 truncate">connex.is · members</span>
            <button type="button" onClick={onZoom} data-testid={`members-zoom-${index}`}
                    className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-white/10 hover:border-white/30 px-2.5 h-6 text-[10px] text-slate-400 hover:text-white transition-colors">
              <Maximize2 className="size-2.5" /> Enlarge
            </button>
          </div>
          <button type="button" onClick={onZoom} className="block w-full text-left cursor-zoom-in">
          <div className="relative overflow-hidden rounded-xl">
            <motion.img
              src={frame.src}
              alt={frame.title}
              loading="lazy"
              width={1400}
              className="block w-full"
              initial={reduce ? false : { filter: "blur(16px) saturate(0.7)", scale: 1.035 }}
              animate={revealed ? { filter: "blur(0px) saturate(1)", scale: 1 } : {}}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            />
            {/* members-only seal */}
            <motion.div
              className="absolute inset-0 grid place-items-center bg-[#0B1220]/45 backdrop-blur-[2px]"
              initial={reduce ? { opacity: 0 } : { opacity: 1 }}
              animate={{ opacity: revealed ? 0 : 1 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              style={{ pointerEvents: "none" }}
            >
              <div className="flex items-center gap-2.5 rounded-full border border-white/20 bg-[#0B1220]/80 px-4 h-10 backdrop-blur-xl">
                <Lock className="size-3.5 text-[#7da9ff]" />
                <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-white">Members only</span>
              </div>
            </motion.div>
            <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-white/10" />
          </div>
          </button>
        </div>
      </motion.div>
    </div>
  );
}
